import { axiosAdminAuthClient, axiosAdminApiClient } from "..";
import { handleError } from "../../utils/errors";

class AuthService {
  register = async (userData) => {
    try {
      const response = await axiosAdminAuthClient.post(
        "/registracija",
        {
          firstName: userData.firstName,
          lastName: userData.lastName,
          phone: userData.phone,
          email: userData.email,
          password: userData.password,
          confirmPassword: userData.confirmPassword,
          terms_accepted: userData.terms_accepted,
          privacy_accepted: userData.privacy_accepted,
          role: userData.role || "user",
        },
        {
          withCredentials: true,
        },
      );

      if (response.status !== 201) {
        throw new Error();
      }

      return response.data;
    } catch (err) {
      console.log("🔧 Original registration error in service:", err);
      console.log("🔧 Error response data:", err.response?.data);
      throw handleError(err, "Error occurred while registering");
    }
  };

  logIn = async (requestData) => {
    try {
      // Extract rememberMe from requestData but don't send it to backend
      const { rememberMe, ...loginData } = requestData;

      const response = await axiosAdminAuthClient.post("/prijava", loginData, {
        withCredentials: true,
      });
      if (response.status !== 200) {
        throw new Error();
      }

      // Add rememberMe back to response for frontend use
      return {
        ...response.data,
        rememberMe,
      };
    } catch (err) {
      throw handleError(err, "Error occurred while logging in");
    }
  };

  logout = async () => {
    try {
      const response = await axiosAdminApiClient.post(
        "auth/logout",
        {},
        {
          withCredentials: true,
        },
      );
      if (response.status !== 200) {
        throw new Error();
      }
      return;
    } catch (err) {
      throw handleError(err, "Error occurred while logging out");
    }
  };

  getProfile = async () => {
    try {
      const response = await axiosAdminApiClient.get("auth/profile", {
        withCredentials: true,
      });
      if (response.status !== 200) {
        throw new Error();
      }

      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while getting profile");
    }
  };

  getMe = async () => {
    try {
      const response = await axiosAdminApiClient.get("auth/me", {
        withCredentials: true,
      });
      if (response.status !== 200) {
        throw new Error();
      }

      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while getting user info");
    }
  };

  updateProfile = async (userData) => {
    try {
      const response = await axiosAdminApiClient.put(
        "auth/profile",
        {
          firstName: userData.firstName,
          lastName: userData.lastName,
          phone: userData.phone,
          email: userData.email,
          city: userData.city,
          address: userData.address,
          country: userData.country,
          postalCode: userData.postalCode,
        },
        {
          withCredentials: true,
        },
      );

      if (response.status !== 200) {
        throw new Error();
      }

      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while updating profile");
    }
  };

  changePassword = async (currentPassword, newPassword, confirmNewPassword) => {
    try {
      const response = await axiosAdminApiClient.put(
        "auth/change-password",
        {
          currentPassword,
          newPassword,
          confirmNewPassword,
        },
        {
          withCredentials: true,
        },
      );

      if (response.status !== 200) {
        throw new Error();
      }

      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while changing password");
    }
  };

  refresh = async () => {
    try {
      const response = await axiosAdminAuthClient.post(
        "/refresh-token",
        {},
        {
          withCredentials: true,
        },
      );
      if (response.status !== 200) {
        throw new Error();
      }

      const { accessToken } = response.data.data;
      return { accessToken };
    } catch (err) {
      throw handleError(err, "Error occurred while refresh token");
    }
  };

  // Admin functions
  getAllUsers = async (page = 1, limit = 10, filters = {}) => {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...filters,
      });

      const response = await axiosAdminApiClient.get(`auth/users?${params}`, {
        withCredentials: true,
      });

      if (response.status !== 200) {
        throw new Error();
      }

      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while getting users");
    }
  };

  getUserStatistics = async () => {
    try {
      const response = await axiosAdminApiClient.get("auth/statistics", {
        withCredentials: true,
      });

      if (response.status !== 200) {
        throw new Error();
      }

      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while getting user statistics");
    }
  };

  deleteUser = async (userId) => {
    try {
      const response = await axiosAdminApiClient.delete(
        `auth/users/${userId}`,
        {
          withCredentials: true,
        },
      );

      if (response.status !== 200) {
        throw new Error();
      }

      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while deleting user");
    }
  };

  forgotPassword = async (email) => {
    try {
      const response = await axiosAdminAuthClient.post(
        "/forgot-password",
        { email },
        {
          withCredentials: true,
        },
      );

      if (response.status !== 200) {
        throw new Error();
      }

      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while requesting password reset");
    }
  };

  resetPassword = async (token, newPassword) => {
    try {
      const response = await axiosAdminAuthClient.post(
        "/reset-password",
        { token, newPassword },
        {
          withCredentials: true,
        },
      );

      if (response.status !== 200) {
        throw new Error();
      }

      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while resetting password");
    }
  };
}

const authService = new AuthService();

export default authService;
